import React, { useCallback, useEffect, useRef } from 'react';
import { useAuthStore } from '../store';
import { translateCurrentShellMessage } from '@/features/language/useI18n';
import { DEFAULT_TOKEN_REFRESH_CONFIG } from '../services/keycloak';

const PROACTIVE_REFRESH_SECONDS = 120;
const REFRESH_RETRY_DELAY_MS = 15000;

/**
 * Props for AuthProvider
 */
interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * AuthProvider component
 * 
 * Initializes authentication and provides auth context to the app.
 * Handles loading state during initialization.
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const initialize = useAuthStore((state) => state.initialize);
  const isLoading = useAuthStore((state) => state.isLoading);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const tokenExpiry = useAuthStore((state) => state.tokenExpiry);
  const authError = useAuthStore((state) => state.error);
  const ensureValidToken = useAuthStore((state) => state.ensureValidToken);
  const initialized = useRef(false);
  const refreshTimeoutRef = useRef<number | null>(null);

  const clearRefreshTimeout = useCallback(() => {
    if (refreshTimeoutRef.current !== null) {
      window.clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }
  }, []);

  const renewAndRescheduleOnFailure = useCallback(async () => {
    const expiryBeforeRefresh = useAuthStore.getState().tokenExpiry;
    await ensureValidToken(PROACTIVE_REFRESH_SECONDS);

    const current = useAuthStore.getState();
    if (
      current.isAuthenticated
      && current.tokenExpiry === expiryBeforeRefresh
      && current.tokenExpiry !== null
    ) {
      clearRefreshTimeout();
      const remainingMs = current.tokenExpiry - Date.now();
      if (remainingMs > 0) {
        refreshTimeoutRef.current = window.setTimeout(
          () => void renewAndRescheduleOnFailure(),
          Math.min(REFRESH_RETRY_DELAY_MS, remainingMs),
        );
      }
    }
  }, [clearRefreshTimeout, ensureValidToken]);

  useEffect(() => {
    // Prevent double initialization in React strict mode
    if (!initialized.current) {
      initialized.current = true;
      initialize();
    }
  }, [initialize]);

  useEffect(() => {
    clearRefreshTimeout();
    if (!isAuthenticated || tokenExpiry === null) {
      return;
    }

    const refreshAt = tokenExpiry - PROACTIVE_REFRESH_SECONDS * 1000;
    refreshTimeoutRef.current = window.setTimeout(
      () => void renewAndRescheduleOnFailure(),
      Math.max(0, refreshAt - Date.now()),
    );

    return clearRefreshTimeout;
  }, [clearRefreshTimeout, isAuthenticated, renewAndRescheduleOnFailure, tokenExpiry]);

  useEffect(() => {
    const checkSession = () => {
      if (useAuthStore.getState().isAuthenticated) {
        void ensureValidToken(DEFAULT_TOKEN_REFRESH_CONFIG.minValidity);
      }
    };
    const checkVisibleSession = () => {
      if (document.visibilityState === 'visible') {
        checkSession();
      }
    };

    window.addEventListener('focus', checkSession);
    window.addEventListener('online', checkSession);
    document.addEventListener('visibilitychange', checkVisibleSession);
    return () => {
      window.removeEventListener('focus', checkSession);
      window.removeEventListener('online', checkSession);
      document.removeEventListener('visibilitychange', checkVisibleSession);
    };
  }, [ensureValidToken]);

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-card">
          <div className="shell-spinner" />
          <h2>{translateCurrentShellMessage('Initializing Session')}</h2>
          <p>{authError?.message ?? translateCurrentShellMessage('Preparing secure portal context')}</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default AuthProvider;
